import { Controller } from "@hotwired/stimulus";
import { formatPhoneNumber } from "../formatters";

/** Delay before validating while typing — keeps slow referral lookups from firing every keystroke. */
const VALIDATE_DEBOUNCE_MS = 500;

/** Do not hit the validation API until the user has entered at least this many characters. */
const MIN_VALIDATE_LENGTH = 4;

export default class extends Controller {
  static targets = ["status", "found", "notFound"];
  static values = { validateUrl: String };

  connect() {
    this.debounceTimer = null;
    this.requestId = 0;
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  validateOnBlur(event) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    const code = event.target.value;
    if (!this.shouldValidate(code)) {
      return;
    }
    this.validateCode(code);
  }

  format(event) {
    const input = event.target.value;

    // format as a referral code if the user enters r- or R- followed by exactly 7 letters (besides l)
    if (input.match(/^r-[a-z0-9]{7,}$/i)) {
      event.target.value = input.toLowerCase();
      this.debouncedValidate(event.target.value);
      return;
    }

    const digitsOnly = input.replace(/\D/g, "");

    // Long all-digit strings are barcodes, not phone numbers (phone path truncates to 10 digits).
    // Keep the user's spacing; only trim ends and cap length.
    if (digitsOnly.length > 10 && /^\d+$/.test(digitsOnly)) {
      event.target.value = input.trim().slice(0, 128);
      this.debouncedValidate(event.target.value);
      return;
    }

    // format as a phone number if the user enters only digits, spaces, dashes, or parentheses
    if (input.startsWith("(") || (input.match(/^[\d\s\-\(\)]+$/) && digitsOnly.length > 0)) {
      if (digitsOnly.length >= 10) {
        const truncatedInput = input.replace(/\D/g, "").substring(0, 10);
        event.target.value = truncatedInput;
        formatPhoneNumber(event);
        this.debouncedValidate(event.target.value);
      } else {
        // Phone-shaped input still in progress: allow only typical phone characters.
        event.target.value = input.replace(/[^\d\s\-\(\)]/g, "");
        clearTimeout(this.debounceTimer);
        this.hideStatus();
      }
      return;
    }

    // Referral code / plate / barcode / etc.: don't strip characters or change case (server matches with ILIKE).
    // Only trim ends and cap length so the field can't grow without bound.
    event.target.value = input.trim().slice(0, 128);

    this.debouncedValidate(event.target.value);
  }

  debouncedValidate(code) {
    clearTimeout(this.debounceTimer);

    if (!this.shouldValidate(code)) {
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.validateCode(code);
    }, VALIDATE_DEBOUNCE_MS);
  }

  shouldValidate(code) {
    const trimmed = code?.trim() ?? "";
    if (trimmed === "") {
      this.hideStatus();
      return false;
    }

    if (trimmed.length < MIN_VALIDATE_LENGTH) {
      this.hideStatus();
      return false;
    }

    if (!this.hasValidateUrlValue || !this.validateUrlValue) {
      return false;
    }

    return true;
  }

  validateCode(code) {
    const currentRequestId = ++this.requestId;
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    const separator = this.validateUrlValue.includes("?") ? "&" : "?";

    fetch(`${this.validateUrlValue}${separator}code=${encodeURIComponent(code)}&_=${Date.now()}`, {
      headers: {
        "X-CSRF-Token": token,
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Validation request failed");
        }
        return response.json();
      })
      .then((data) => {
        if (currentRequestId !== this.requestId) return;
        this.showStatus(data.status, data.referrer_name, data.match_type);
      })
      .catch(() => {
        if (currentRequestId !== this.requestId) return;
        this.showStatus("error");
      });
  }

  showStatus(status, referrerName, matchType) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.classList.remove("hidden");

    if (this.hasFoundTarget) {
      this.foundTarget.classList.add("hidden");
    }
    if (this.hasNotFoundTarget) {
      this.notFoundTarget.classList.add("hidden");
    }

    if (status === "found" && this.hasFoundTarget) {
      this.foundTarget.classList.remove("hidden");
      const matchLabel = this.getMatchTypeLabel(matchType);
      this.foundTarget.textContent = referrerName
        ? `Referrer found: ${referrerName} (${matchLabel})`
        : `Referrer found (${matchLabel})`;
    } else if (
      (status === "not_found" || status === "error" || status === "disabled") &&
      this.hasNotFoundTarget
    ) {
      this.notFoundTarget.classList.remove("hidden");
      if (status === "error") {
        this.notFoundTarget.textContent = "Unable to verify - please try again";
      } else if (status === "disabled") {
        this.notFoundTarget.textContent = "Referral validation is not available";
      } else {
        this.notFoundTarget.textContent = "No referrer found - check your entry";
      }
    }
  }

  hideStatus() {
    if (!this.hasStatusTarget) return;

    this.statusTarget.classList.add("hidden");
    if (this.hasFoundTarget) {
      this.foundTarget.classList.add("hidden");
    }
    if (this.hasNotFoundTarget) {
      this.notFoundTarget.classList.add("hidden");
    }
  }

  getMatchTypeLabel(matchType) {
    switch (matchType) {
      case "phone":
        return "phone";
      case "referral_code":
        return "code";
      case "license_plate":
        return "license plate";
      case "barcode":
        return "barcode";
      default:
        return "match";
    }
  }
}
